/**
 * One payer's card in the Auth Letter Payer Config.
 *
 * Instead of editing every field, the operator uploads a JSON prompt for the
 * payer (ingested to the backend / Firestore config on upload) and can Test it.
 * An enable/disable toggle turns the payer's config on or off without deleting
 * it. Results of a test are shown as CSV in {@link PromptTestPanel}.
 * @module PayerConfigCard
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useRef, useState } from "react";
import ToolTipUsingPortal from "../../../../../components/toolTipUsingPortal/toolTipUsingPortal";
import { CopyIcon } from "../../../../../svg/copy-icon";
import PayerPromptDetails from "./PayerPromptDetails";
import PromptTestPanel from "./PromptTestPanel";

type Dict = Record<string, unknown>;

export interface UploadedPrompt {
  j_code_info?: Dict;
  prompt_provider_info?: Dict;
}

interface Props {
  name: string;
  jcode: Dict;
  provider: Dict;
  enabled: boolean;
  unsaved?: boolean;
  saving: boolean;
  onToggle: (enabled: boolean) => void;
  /**
   * Persists an uploaded prompt after the operator picks target orgs.
   *
   * @return `"saved"` on success, `"cancelled"` if the org picker was dismissed,
   *   `"failed"` when the write failed
   */
  onUpload: (
    parsed: UploadedPrompt,
    fileName: string,
  ) => Promise<"saved" | "cancelled" | "failed">;
  /**
   * Opens the org picker to copy this payer's current NYCBS prompt elsewhere.
   * Omitted for unsaved drafts.
   */
  onSyncToOrgs?: () => void;
}

const PayerConfigCard = ({
  name,
  jcode,
  provider,
  enabled,
  unsaved = false,
  saving,
  onToggle,
  onUpload,
  onSyncToOrgs,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [preparingUpload, setPreparingUpload] = useState(false);
  const [showUnsavedTip, setShowUnsavedTip] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const unsavedPillRef = useRef<HTMLDivElement>(null);

  const isDefault = name.trim().toLowerCase() === "default";

  /**
   * Parses the chosen JSON file, then hands it to the parent to pick orgs.
   * Success/failure feedback is shown via toasts (parent + local parse errors).
   *
   * @param file - File from the hidden input, or `undefined` if cleared
   */
  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setPreparingUpload(true);
    try {
      const parsed = JSON.parse(await file.text());
      if (typeof parsed !== "object" || Array.isArray(parsed) || !parsed) {
        throw new Error("JSON must be an object.");
      }
      const hasSections =
        "j_code_info" in parsed || "prompt_provider_info" in parsed;
      const result = await onUpload(
        hasSections ? parsed : { j_code_info: parsed },
        file.name,
      );
      if (result === "failed") {
        toast.error("Save failed — the prompt was not stored.");
      }
      // saved → parent toast; cancelled → stay quiet
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Invalid JSON file.");
    } finally {
      setPreparingUpload(false);
    }
  };

  return (
    <div
      className={`rounded-lg border ${
        enabled
          ? "border-primaryGray-14"
          : "border-primaryGray-14 bg-primaryGray-16"
      }`}
    >
      <div className="flex items-center gap-3 p-4">
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-sm text-primaryGray-6"
          title={open ? "Collapse" : "Expand"}
        >
          {open ? "▾" : "▸"}
        </button>
        <span
          className={`text-sm font-bold ${
            enabled ? "text-primaryGray-1" : "text-primaryGray-6 line-through"
          }`}
        >
          {isDefault ? "Default" : name}
        </span>
        {saving || preparingUpload ? (
          <span className="text-tiny font-semibold text-primaryGray-6">
            {preparingUpload ? "Choose organizations…" : "Saving…"}
          </span>
        ) : null}

        <div className="ml-auto flex items-center gap-3">
          {unsaved ? (
            <div
              ref={unsavedPillRef}
              onMouseEnter={() => setShowUnsavedTip(true)}
              onMouseLeave={() => setShowUnsavedTip(false)}
              className="rounded-full bg-primaryGray-16 px-2 py-1 text-tiny font-semibold text-primaryGray-6"
            >
              Not saved
              <ToolTipUsingPortal
                placement="bottom-right"
                showTooltip={showUnsavedTip}
                parentRef={unsavedPillRef}
                wrapText
                maxWidth="280px"
              >
                <div className="max-w-[280px] whitespace-normal text-tiny leading-snug text-primaryGray-1">
                  Added locally — upload a prompt JSON to save. Until then it
                  uses the <span className="font-semibold">Default</span> prompt
                  and is discarded on refresh.
                </div>
              </ToolTipUsingPortal>
            </div>
          ) : isDefault ? (
            <span
              className="text-tiny font-semibold text-primaryGreen-4"
              title="The Default prompt is always active as the fallback"
            >
              Always on
            </span>
          ) : (
            <label
              className="flex cursor-pointer items-center gap-2 text-tiny font-semibold text-primaryGray-1"
              title={enabled ? "Turn this payer OFF" : "Turn this payer ON"}
            >
              <span
                className={
                  enabled ? "text-primaryGreen-4" : "text-primaryGray-6"
                }
              >
                {enabled ? "On" : "Off"}
              </span>
              <span
                onClick={() => onToggle(!enabled)}
                className={`relative inline-block h-5 w-9 rounded-full transition-colors ${
                  enabled ? "bg-primaryGreen-4" : "bg-primaryGray-12"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
                    enabled ? "left-[1.15rem]" : "left-0.5"
                  }`}
                />
              </span>
            </label>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="rounded border border-primaryGray-14 bg-white px-3 py-1.5 text-tiny font-semibold text-primaryGray-1 hover:bg-primaryGray-16"
            title="Upload a prompt JSON"
          >
            Upload JSON
          </button>
          <button
            onClick={() => setShowTest((v) => !v)}
            className="rounded border border-gray-800 bg-gray-800 px-3 py-1.5 text-tiny font-semibold text-white hover:bg-gray-700"
          >
            {showTest ? "Hide test" : "Test prompt"}
          </button>
          {!unsaved && onSyncToOrgs && (
            <button
              type="button"
              onClick={onSyncToOrgs}
              disabled={saving || preparingUpload}
              className="rounded border border-primaryGray-14 bg-white p-1.5 hover:bg-primaryGray-16 disabled:cursor-not-allowed disabled:opacity-50"
              title="Copy this NYCBS prompt to other organizations"
              aria-label={`Copy ${name} prompt to other organizations`}
            >
              <CopyIcon width="16" height="16" />
            </button>
          )}
        </div>
      </div>

      {showTest && (
        <div className="border-t border-primaryGray-14 p-4">
          <PromptTestPanel
            payerName={name}
            onClose={() => setShowTest(false)}
          />
        </div>
      )}

      {open && (
        <PayerPromptDetails
          name={name}
          jcode={jcode}
          provider={provider}
          onFileSelected={handleFile}
        />
      )}
    </div>
  );
};

export default PayerConfigCard;
